/**
 * `AgencyAccessPanel` — the client-side view of attached agencies
 * (ADR-0237 §6). Lives in Account settings and is visible to every
 * account (so a merchant whose team happens to be an agency's client
 * can see + revoke that relationship from the same place).
 *
 * Below it, an "Agency" block renders ONLY when
 * `features.agency === true`:
 *   - White label status: "On" or "Not on your plan — contact us".
 *     No prices in Slice 1 (per the handoff).
 */

import { useCallback, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';

import type { AgencyAccessListItemWire } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { useAgency } from '../../app/agency-context.js';
import { useAuth } from '../../app/auth-context.js';
import {
  listAgencyAccess,
  revokeAgencyAccess,
} from '../../api/agency.js';
import { useC } from '../../lib/theme.js';
import {
  Card,
  ConfirmDialog,
  Pill,
} from '../../components/atoms/index.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; agencies: readonly AgencyAccessListItemWire[] }
  | { kind: 'error'; message: string };

export function AgencyAccessPanel(): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useAuth();
  const { features } = useAgency();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [confirm, setConfirm] = useState<AgencyAccessListItemWire | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const role = auth.state.kind === 'authenticated' ? auth.state.user.role : null;
  const canRevoke = role === 'owner' || role === 'admin';

  const refresh = useCallback(async (): Promise<void> => {
    if (http === null) return;
    setState({ kind: 'loading' });
    const r = await listAgencyAccess(http);
    if (r.kind === 'ok') {
      setState({ kind: 'ok', agencies: r.agencies });
    } else {
      setState({ kind: 'error', message: r.message });
    }
  }, [http]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const doRevoke = async (): Promise<void> => {
    if (http === null || confirm === null || pending) return;
    setPending(true);
    setError(null);
    const r = await revokeAgencyAccess(http, confirm.link_id);
    setPending(false);
    if (r.kind === 'ok' || r.kind === 'not_found') {
      setConfirm(null);
      void refresh();
      return;
    }
    if (r.kind === 'forbidden') {
      setError('You do not have permission to revoke this link.');
      return;
    }
    setError(r.message);
  };

  return (
    <div className="space-y-6">
      <Card className="p-5 sm:p-6" id="agency-access">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 12,
          }}
        >
          Agency access
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            lineHeight: 1.5,
            marginBottom: 16,
          }}
        >
          Agencies that can act inside your account. Revoke any time — the
          agency loses access immediately and is signed back out of your
          account on their next click.
        </div>

        {state.kind === 'loading' ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            Loading…
          </div>
        ) : null}

        {state.kind === 'error' ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>
            {state.message}
          </div>
        ) : null}

        {state.kind === 'ok' && state.agencies.length === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            No agencies have access.
          </div>
        ) : null}

        {state.kind === 'ok' && state.agencies.length > 0 ? (
          <ul className="space-y-2 list-none p-0 m-0">
            {state.agencies.map((a) => (
              <li
                key={a.link_id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 px-3 py-2"
                style={{
                  background: C.surface2,
                  border: `1px solid ${C.borderSoft}`,
                  borderRadius: 2,
                }}
              >
                <div className="min-w-0 flex-1">
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.text,
                      fontWeight: 500,
                    }}
                  >
                    {a.agency_name}
                  </div>
                  <div
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.dim,
                      marginTop: 2,
                    }}
                  >
                    {a.provenance === 'agency_created'
                      ? 'They built this account'
                      : 'You approved them'}
                    {' · role '}
                    {a.granted_role}
                    {' · since '}
                    {a.created_at.slice(0, 10)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Pill tone={a.status === 'active' ? 'lime' : 'amber'}>{a.status}</Pill>
                  {canRevoke ? (
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setConfirm(a);
                      }}
                      className="flex items-center gap-1"
                      style={{
                        background: 'transparent',
                        color: C.coralFg,
                        fontFamily: sansFont,
                        fontSize: 12,
                        padding: '6px 10px',
                        border: `1px solid ${C.coralFg}`,
                        borderRadius: 2,
                        minHeight: 36,
                      }}
                      aria-label={`Revoke ${a.agency_name}`}
                    >
                      <Trash2 size={12} /> Revoke
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        {error !== null ? (
          <div
            className="mt-3"
            style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}
          >
            {error}
          </div>
        ) : null}
      </Card>

      {features?.agency === true ? <AgencyAddOnBlock whiteLabel={features.white_label} /> : null}

      <ConfirmDialog
        open={confirm !== null}
        title={confirm !== null ? `Revoke ${confirm.agency_name}?` : 'Revoke agency?'}
        body={
          <>
            Members of this agency lose access immediately on their next request. The link row is kept for audit history.
          </>
        }
        confirmLabel={pending ? 'Revoking…' : 'Revoke access'}
        confirmTone="coral"
        onConfirm={() => void doRevoke()}
        onClose={() => {
          if (pending) return;
          setConfirm(null);
          setError(null);
        }}
      />
    </div>
  );
}

function AgencyAddOnBlock({ whiteLabel }: { whiteLabel: boolean }): JSX.Element {
  const C = useC();
  return (
    <Card className="p-5 sm:p-6" id="agency">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 12,
        }}
      >
        Agency
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
            White label
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              marginTop: 2,
              lineHeight: 1.5,
            }}
          >
            Replace Sill branding on reports and report emails prepared through this account.
          </div>
        </div>
        {whiteLabel ? (
          <Pill tone="lime">On</Pill>
        ) : (
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
            Not on your plan — contact us.
          </span>
        )}
      </div>
    </Card>
  );
}
